/**
 * Manages the rendering of non-chart widgets like tables and value labels.
 * It is decoupled from data fetching and reacts to events to update its content.
 */
namespace distill.dashboards.widgets {
    export function render(element: JQuery, component: IComponent): void {
        const chartEl = element.find('.chart').empty();

        const definition = componentManager.getDefinition(component.type);
        if (!definition)
            return;

        // Widget templates center chart content (value labels, chat) - the data table needs
        // standard left alignment.
        if (component.type === ComponentType.table) {
            chartEl.removeClass('text-center');
            return renderTable(element, chartEl, component as IDashboardComponent);
        }
        else if (component.type === ComponentType.distillQuery)
            return renderDistillQuery(element, chartEl, component as IDashboardComponent);
        else if (component.type === ComponentType.valueLabel)
            return renderValueLabel(element, chartEl, component);
        else if (component.type === ComponentType.heading)
            return renderHeading(element, chartEl, component);
    }

    type ValueLabelState = 'Normal' | 'Warning' | 'Critical';

    interface IValueLabelMetrics {
        mainValue: string;
        rawVal: number | null;
        trendPercent: number | null;
        trendDiff: number | null;
        sparklineData: (number | null)[];
        sparklineLabels: string[]; // hold formatted dates
        progressPercent: number | null;
        state: ValueLabelState;
    }

    interface IValueLabelStyle {
        active: { bg: string; text: string; badgeBg: string; badgeText: string; };
        applyToCard: boolean;
        applyToText: boolean;
        applyToValue: boolean;
        finalCardBg: string;
        finalCardText: string;
    }

    function _formatValueLabel(val: any, component: IDashboardComponent, isAbsoluteTrend: boolean = false): string {
        const prefix = component.styling?.valuePrefix || '';
        const suffix = component.styling?.valueSuffix || '';

        if (val === null || val === undefined)
            return '-';

        if (typeof val === 'number') {
            const num = isAbsoluteTrend ? Math.abs(val) : val;
            const options: Intl.NumberFormatOptions = {};
            if (component.styling?.valueDecimals != null && component.styling.valueDecimals >= 0) {
                options.minimumFractionDigits = component.styling.valueDecimals;
                options.maximumFractionDigits = component.styling.valueDecimals;
            }
            return prefix + num.toLocaleString(undefined, options) + suffix;
        }
        return prefix + val + suffix;
    }

    function _processValueLabelData(component: IDashboardComponent, rawData: IDataQueryResult | null): Omit<IValueLabelMetrics, 'mainValue' | 'progressPercent' | 'state'> {
        let rawVal: number | null = null;
        let trendPercent: number | null = null;
        let trendDiff: number | null = null;
        let sparklineData: (number | null)[] = [];
        let sparklineLabels: string[] = [];

        if (rawData && rawData.ObjectResult && rawData.ObjectResult.length > 0) {
            const rows = rawData.ObjectResult;
            const dataQuery0 = (component as any).dataQuery0;
            const metricDef = dataQuery0?.aggregation?.metrics?.[0];
            const headerKey = metricDef ? distill.dashboards.getExpectedMetricHeader(component, dataQuery0, metricDef, 0) : rawData.Headers[0];
            const metricFunction = metricDef?.function || 'Sum';

            const isMax = metricFunction === 'Max';
            const isMin = metricFunction === 'Min';
            const isAvg = metricFunction === 'Avg';
            const isDistinct = metricFunction === 'CountDistinct';

            if (headerKey) {
                const isHistory = !!dataQuery0?.aggregation?.groupBy?.columnId;
                const groupByCol = dataQuery0?.aggregation?.groupBy;
                const timeGrain = groupByCol?.timeGrain || distill.dashboards.InsightTimeGrain.None;

                let orderedValues: (number | null)[] = [];
                let orderedLabels: string[] = [];

                const calcAggregate = (vals: number[]) => {
                    if (isMax) return Math.max(...vals);
                    if (isMin) return Math.min(...vals);
                    if (isAvg) return vals.reduce((a, b) => a + b, 0) / vals.length;
                    if (isDistinct) return Math.max(...vals);
                    return vals.reduce((a, b) => a + b, 0);
                };

                orderedValues = rows.map(r => { const v = Number(r[headerKey]); return isNaN(v) ? null : v; });

                const allValues = orderedValues.filter(v => v !== null) as number[];
                const mode = component.layout?.summaryMode || 'Latest Value';

                if (mode === 'Latest Value') {
                    if (allValues.length > 0)
                        rawVal = allValues[allValues.length - 1];

                    if (isHistory && allValues.length > 1) {
                        const curr = allValues[allValues.length - 1];
                        const prev = allValues[allValues.length - 2];
                        trendDiff = curr - prev;
                        if (prev !== 0)
                            trendPercent = (trendDiff / Math.abs(prev)) * 100;
                    }
                } else {
                    rawVal = calcAggregate(allValues);
                }

                if (isHistory) {
                    sparklineData = orderedValues;
                    sparklineLabels = orderedLabels;
                }
            }
        }

        return { rawVal, trendPercent, trendDiff, sparklineData, sparklineLabels };
    }

    function _calculateValueLabelState(component: IDashboardComponent, rawVal: number | null): ValueLabelState {
        if (!component.styling?.enableThresholds || rawVal === null)
            return 'Normal';

        let valueToCompare = rawVal;
        const usePercent = component.styling.thresholdType === 'PercentOfTarget';

        if (usePercent && component.targetValue)
            valueToCompare = (rawVal / component.targetValue) * 100;

        if (usePercent && !component.targetValue)
            return 'Normal';

        const t1 = component.styling.warningThreshold ?? 0;
        const t2 = component.styling.dangerThreshold ?? 0;
        const isInverted = component.styling.thresholdLogic === 'Inverted';

        if (!isInverted) {
            const criticalLimit = Math.min(t1, t2);
            const warningLimit = Math.max(t1, t2);
            if (valueToCompare < criticalLimit) return 'Critical';
            if (valueToCompare < warningLimit) return 'Warning';
        } else {
            const criticalLimit = Math.max(t1, t2);
            const warningLimit = Math.min(t1, t2);
            if (valueToCompare > criticalLimit) return 'Critical';
            if (valueToCompare > warningLimit) return 'Warning';
        }

        return 'Normal';
    }

    function _getValueLabelStyles(component: IDashboardComponent, state: ValueLabelState, trendValue: number): IValueLabelStyle {
        const stateColors = {
            Normal: { bg: component.bgColor || '#ffffff', text: component.styling?.valueColor || '#2c3e50', badgeBg: '#dcfce7', badgeText: '#166534' },
            Warning: { bg: '#fff7ed', text: '#c2410c', badgeBg: '#ffedd5', badgeText: '#9a3412' },
            Critical: { bg: '#fef2f2', text: '#b91c1c', badgeBg: '#fee2e2', badgeText: '#991b1b' }
        };

        const isPositive = trendValue >= 0;
        if (state === 'Normal') {
            const isGood = component.styling?.trendColorMode === 'Inverted' ? !isPositive : isPositive;
            if (!isGood) {
                stateColors.Normal.badgeBg = '#fee2e2';
                stateColors.Normal.badgeText = '#991b1b';
            }
        }

        if (component.styling?.applyConditionTo === 'Card' && state !== 'Normal') {
            stateColors.Warning = { bg: '#ea580c', text: '#ffffff', badgeBg: 'rgba(255,255,255,0.2)', badgeText: '#fff' };
            stateColors.Critical = { bg: '#dc2626', text: '#ffffff', badgeBg: 'rgba(255,255,255,0.2)', badgeText: '#fff' };
        }

        const active = stateColors[state];
        const applyToCard = component.styling?.applyConditionTo === 'Card' && state !== 'Normal';
        const applyToText = component.styling?.applyConditionTo === 'Text' && state !== 'Normal';
        const applyToValue = component.styling?.applyConditionTo === 'Value' && state !== 'Normal';
        const finalCardBg = applyToCard ? active.bg : (component.bgColor || '#fff');
        const finalCardText = applyToCard ? active.text : (component.styling?.valueColor || '#2c3e50');

        return { active, applyToCard, applyToText, applyToValue, finalCardBg, finalCardText };
    }

    function _buildValueLabelDOM(container: JQuery, component: IDashboardComponent, metrics: IValueLabelMetrics, styles: IValueLabelStyle, isDesigner: boolean) {
        container.empty();
        const $card = $('<div>').addClass('metric-card');
        $card.addClass('card-shadow');
        if (styles.applyToCard) $card.addClass('card-solid');
        const hasTrend = (component.layout?.showTrend !== false) && (metrics.trendPercent !== null || metrics.trendDiff !== null);
        if (!hasTrend) $card.addClass('mc-centered');
        $card.css({ 'background-color': styles.finalCardBg, 'color': styles.finalCardText });
        if (metrics.state !== 'Normal' && !styles.applyToCard) $card.css('border', `1px solid ${metrics.state === 'Warning' ? '#fdba74' : '#fca5a5'}`);

        // Label & Hint Logic
        if (!isDesigner) {
            const labelColor = (styles.applyToText && !styles.applyToCard) ? styles.active.text : 'inherit';
            const labelText = component.label?.eng || component.name || "Metric";
            const $label = $('<div>').addClass('mc-label').text(labelText).css('color', labelColor);
            if (component.labelStyle) {
                if (component.labelStyle.color)
                    $label.css('color', component.labelStyle.color);
                if (component.labelStyle.size)
                    $label.css('font-size', component.labelStyle.size);
                if (component.labelStyle.decor === 'Bold')
                    $label.css('font-weight', 'bold');
                if (component.labelStyle.decor === 'Italic')
                    $label.css('font-style', 'italic');
            }
            $card.append($label);

            if (component.hint?.eng) {
                const $hint = $('<div>').addClass('mc-hint').text(component.hint.eng);
                if (component.hintStyle) {
                    if (component.hintStyle.color)
                        $hint.css('color', component.hintStyle.color);
                    if (component.hintStyle.size)
                        $hint.css('font-size', component.hintStyle.size);
                    if (component.hintStyle.decor === 'Bold')
                        $hint.css('font-weight', 'bold');
                    if (component.hintStyle.decor === 'Italic')
                        $hint.css('font-style', 'italic');
                } else {
                    $hint.css('color', labelColor).css('opacity', '0.6');
                }
                $card.append($hint);
            }
        }

        // Value Row Logic
        const $valueRow = $('<div>').addClass('mc-value-row');
        const valueColor = (styles.applyToValue || (styles.applyToText && !styles.applyToCard)) ? styles.active.text : 'inherit';
        const $value = $('<div>').addClass('mc-value').text(metrics.mainValue).css('color', valueColor);
        $valueRow.append($value);

        // Trend Logic
        if (hasTrend) {
            const trendValue = metrics.trendPercent ?? metrics.trendDiff ?? 0;
            const isPositive = trendValue >= 0;
            const arrow = isPositive ? '↑' : '↓';
            const displayMode = component.layout?.trendDisplayMode || 'Percent';
            let text = '';
            const pctText = metrics.trendPercent !== null ? `${Math.abs(metrics.trendPercent).toFixed(1)}%` : '-';
            const absText = metrics.trendDiff !== null ? _formatValueLabel(metrics.trendDiff, component, true) : '-';
            if (displayMode === 'Percent')
                text = `${arrow} ${pctText}`;
            else if (displayMode === 'Absolute')
                text = `${arrow} ${absText}`;
            else if (displayMode === 'Both')
                text = `${arrow} ${pctText} (${absText})`;

            const $trendBadge = $('<div>').addClass('mc-trend-badge').text(text);
            $trendBadge.css({ 'background-color': styles.active.badgeBg, 'color': styles.active.badgeText });
            $valueRow.append($trendBadge);
        }
        $card.append($valueRow);

        // Progress Bar Logic
        if (component.layout?.showProgressBar && metrics.progressPercent !== null) {
            const $footer = $('<div>').addClass('mc-footer');
            const $progressContainer = $('<div>').addClass('mc-progress-container mc-progress-bg');
            const barColor = (styles.applyToCard) ? '#ffffff' : (component.styling?.valueColor || '#2c3e50');
            const $bar = $('<div>').addClass('mc-progress-fill').css({ 'width': `${metrics.progressPercent}%`, 'background-color': barColor });
            $progressContainer.append($bar);
            $footer.append($progressContainer);
            $card.append($footer);
        }

        if (component.layout?.kpiStyle === 'Sparkline' && metrics.sparklineData.length > 1) {
            const sparkId = `spark_${component.id}`;
            const $sparkEl = $(`<div id="${sparkId}"></div>`).addClass('mc-sparkline');
            $card.append($sparkEl);
            setTimeout(() => {
                const el = document.getElementById(sparkId) as HTMLDivElement;
                if (el) {
                    // Check if instance exists to prevent race conditions on rapid re-renders
                    let chart = echarts.getInstanceByDom(el);
                    if (!chart) chart = echarts.init(el);

                    const baseColor = component.styling?.sparklineColor || '#e0e7ff';
                    const finalSparkColor = (styles.applyToCard) ? '#ffffff' : baseColor;

                    chart.setOption({
                        tooltip: {
                            trigger: 'axis',
                            formatter: (params: any) => {
                                const label = params[0].name;
                                const value = params[0].value;
                                const valDisplay = value !== null ? value.toLocaleString() : 'No data';
                                return `<div style="font-size:12px;font-weight:bold">${label}</div><div>${valDisplay}</div>`;
                            }
                        },
                        grid: { left: 5, right: 5, bottom: 5, top: '20%' },
                        xAxis: {
                            type: 'category',
                            data: metrics.sparklineLabels,
                            show: false,
                            boundaryGap: false
                        },
                        yAxis: { type: 'value', show: false, scale: true },
                        series: [{
                            data: metrics.sparklineData as any[],
                            type: 'line',
                            smooth: true,
                            showSymbol: false,
                            lineStyle: { width: 2, color: finalSparkColor },
                            areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: finalSparkColor }, { offset: 1, color: 'rgba(255, 255, 255, 0)' }]) }
                        }]
                    });

                    // Simple resize hook
                    new ResizeObserver(() => chart.resize()).observe(el);
                }
            }, 0);
        }

        container.append($card);
    }
    function _setupValueLabelResponsiveScaling(element: JQuery) {
        const $card = element.find('.metric-card');
        if (!$card.length)
            return;

        const existingObserver = element.data('resizeObserver');
        if (existingObserver instanceof ResizeObserver) {
            existingObserver.disconnect();
        }

        const updateScale = () => {
            const width = element.width() || 250;
            const height = element.height() || 150;
            const scaleW = width / 250;
            const scaleH = height / 150;
            const scale = Math.min(scaleW, scaleH);
            const fontSize = Math.max(10, Math.min(18, 16 * scale));
            $card.css('font-size', `${fontSize}px`);
        };

        const resizeObserver = new ResizeObserver(() => updateScale());
        resizeObserver.observe(element.get(0));

        element.data('resizeObserver', resizeObserver);

        updateScale();
    }

    function renderValueLabel(element: JQuery, container: JQuery, component: IComponent) {
        const dataQueryPropName = 'dataQuery0';
        const cacheKey = `${component.id}.${dataQueryPropName}`;
        const rawData = dataManager.getCachedResult(cacheKey);
        const dashComponent = component as IDashboardComponent;

        const $header = element.find('.card-header');
        const isDesigner = $('body').hasClass('designerv2');
        if (isDesigner) {
            $header.removeClass('d-none');
        } else {
            $header.addClass('d-none');
            element.find('.card-body').css('height', '100%');
        }

        const { rawVal, trendPercent, trendDiff, sparklineData, sparklineLabels } = _processValueLabelData(dashComponent, rawData);

        const metrics: IValueLabelMetrics = {
            rawVal: rawVal,
            trendPercent: trendPercent,
            trendDiff: trendDiff,
            sparklineData: sparklineData,
            sparklineLabels: sparklineLabels,
            mainValue: _formatValueLabel(rawVal, dashComponent),
            progressPercent: (dashComponent.targetValue && rawVal !== null) ? Math.min((rawVal / dashComponent.targetValue) * 100, 100) : null,
            state: _calculateValueLabelState(dashComponent, rawVal)
        };

        const trendValue = trendPercent ?? trendDiff ?? 0;
        const styles = _getValueLabelStyles(dashComponent, metrics.state, trendValue);

        _buildValueLabelDOM(container, dashComponent, metrics, styles, isDesigner);
        _setupValueLabelResponsiveScaling(element);
    }

    function renderHeading(element: JQuery, chartEl: JQuery, component: IComponent) {
        const dashComponent = component as IDashboardComponent;
        chartEl.empty();

        const $container = $('<div>').addClass('heading-content');

        $container.css({
            'justify-content': getFlexAlign(dashComponent.layout?.headingAlign),
            'background-color': dashComponent.bgColor || 'transparent'
        });

        const $text = $('<span>').text(dashComponent.headingText || 'New Heading');
        $text.css({
            'color': dashComponent.styling?.headingColor || '#2c3e50',
            'font-size': (dashComponent.styling?.headingSize || 24) + 'px',
            'font-weight': dashComponent.styling?.isBold ? 'bold' : 'normal',
            'font-style': dashComponent.styling?.isItalic ? 'italic' : 'normal'
        });

        $container.append($text);
        chartEl.append($container);
    }

    export function getFlexAlign(align?: 'Left' | 'Center' | 'Right'): string {
        if (align === 'Right')
            return 'flex-end';
        if (align === 'Center')
            return 'center';

        return 'flex-start';
    }

    let $openFilterPopover: JQuery | null = null;

    // Selection + option-cache keys for the table's column filters are namespaced with this
    // prefix so the global-filter change hook can clear/prune exactly this family.
    const TABLE_COLUMN_KEY_PREFIX = 'table-column:';

    function closeTableFilterPopover(): void {
        if ($openFilterPopover === null)
            return;

        $openFilterPopover.remove();
        $openFilterPopover = null;
        $(document).off('mouseup.table-filter-popover');
    }

    function renderTable(element: JQuery, chartEl: JQuery, component: IDashboardComponent){
        const dataQueryPropName = 'dataQuery0';
        const dataQuery = (component as any)[dataQueryPropName] as IDataQuery;
        const cacheKey = `${component.id}.${dataQueryPropName}`;
        const rawData = dataManager.getCachedResult(cacheKey);

        const isDesigner = $('body').hasClass('designerv2');

        // A component background color means the table sits on a fill rather than the card
        // surface: the default stripes, rules, scrollbar and text colors (tuned for a light
        // card) would read wrong over it. Derive them all from the fill instead - darker on a
        // light fill, lighter on a dark one - and expose them as custom properties on the chart
        // so the table, list, pager and empty state can all read them (see _ComponentTemplates.scss).
        const bgColor = component.bgColor;
        const hasBgColor = !!bgColor && bgColor !== 'transparent';
        const isDarkBg = hasBgColor && !appn.utils.isLightColor(bgColor!);
        const tableStripeColor = isDarkBg ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
        const tableBorderColor = isDarkBg ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.12)';
        const scrollbarThumbColor = isDarkBg ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.3)';
        const textColor = isDarkBg ? '#ffffff' : '#212529';
        const mutedTextColor = isDarkBg ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.55)';
        const inputBgColor = isDarkBg ? 'rgba(255, 255, 255, 0.12)' : '#ffffff';

        // Shared by the chart and the (body-level) filter popover, which can't inherit from it.
        const tintProperties = {
            '--distill-fill': bgColor!,
            '--distill-table-stripe': tableStripeColor,
            '--distill-table-border': tableBorderColor,
            '--distill-scrollbar-thumb': scrollbarThumbColor,
            '--distill-text-color': textColor,
            '--distill-muted-text-color': mutedTextColor,
            '--distill-input-bg': inputBgColor
        };

        chartEl.toggleClass('tinted', hasBgColor);
        if (hasBgColor)
            chartEl.css(tintProperties);

        const columnFilters: { [header: string]: IColumnFilterState } = !isDesigner && element.data('columnFilters') !== undefined
            ? element.data('columnFilters')
            : {};
        if (!isDesigner)
            element.data('columnFilters', columnFilters);

        // Register the paging window before any refresh, so even an externally-triggered
        // first fetch (initial load) comes back windowed with a total count.
        if (!isDesigner && !dataManager.hasTableWindow(component.id))
            dataManager.setTableWindowOffset(component.id, 0);

        const isActiveFilter = (state: IColumnFilterState | undefined): boolean =>
            !!(state && ((state.values && state.values.length > 0) || state.from || state.to));

        // The dataset column for a header (projection / groupBy / breakDownBy column) —
        // resolves both the typed metadata and the exact column identity (datasetIndex +
        // columnId) used to scope filters.
        const getColumnForHeader = (header: string): { metricIndex: number | null, identifier: IDataQueryColumnIdentifier | null, meta: IDatasetColumnMeta } | null => {
            const datasetRefs = getDatasetRefsInDataQuery(dataQuery);
            const resolve = (identifier: IDataQueryColumnIdentifier | undefined): { metricIndex: number | null, identifier: IDataQueryColumnIdentifier | null, meta: IDatasetColumnMeta } | null => {
                if (!identifier)
                    return null;

                const meta = getContextualColumnMeta(identifier.datasetIndex, identifier.columnId, datasetRefs, designer.getDatasets()).columnMeta;
                return meta ? { metricIndex: null, identifier: identifier, meta: meta } : null;
            };

            if (dataQuery.aggregation) {
                if (dataQuery.aggregation.groupBy && getHeaderName(dataQuery.aggregation.groupBy, component) === header)
                    return resolve(dataQuery.aggregation.groupBy);
                if (dataQuery.aggregation.breakDownBy && getHeaderName(dataQuery.aggregation.breakDownBy, component) === header)
                    return resolve(dataQuery.aggregation.breakDownBy);

                // A metric header (count/sum — no dataset column) resolves to its metrics-list
                // index; its filter routes to the server's HAVING filter instead of WHERE.
                const metrics = dataQuery.aggregation.metrics || [];
                for (let m = 0; m < metrics.length; m++) {
                    if (getExpectedMetricHeader(component, dataQuery, metrics[m], m) === header)
                        return { metricIndex: m, identifier: null, meta: { id: '', displayName: header, designation: null, type: 'Number' } };
                }
            }

            const proj = (dataQuery.projection?.columns || []).find(c => getHeaderName(c, component) === header);
            return proj ? resolve(proj) : null;
        };

        // Numeric columns get BOTH filter UIs (value list + range). Beyond the typed check,
        // metric columns (counts, sums — no dataset column) are inferred from the data.
        const isNumericColumn = (header: string): boolean => {
            if (getColumnForHeader(header)?.meta.type === 'Date')
                return false;
            if (getColumnForHeader(header)?.meta.type === 'Number')
                return true;

            const values = allRows.map(row => row[header]).filter(v => v !== null && v !== undefined && v !== '');
            return values.length > 0 && values.every(v => !isNaN(Number(v)));
        };

        // Builds the filter selection for a column — ONE selection referencing the column by
        // its deterministic filter id, carrying the value list and/or range (the server ANDs
        // both). Range bounds go as picked: date columns get the exclusive end treatment
        // (next UTC day) server-side, numbers get inclusive bounds. Operator semantics derive
        // server-side from the selection's shape — the client never sends operators.
        const buildFilters = (header: string): IDashFilterSelection[] => {
            const state = columnFilters[header];
            if (!isActiveFilter(state))
                return [];

            const resolved = getColumnForHeader(header);
            if (!resolved)
                return [];

            // Metric (HAVING) scope: numeric range only — a metric has no distinct values to list.
            if (resolved.metricIndex !== null) {
                const metricFilterId = (dataQuery.aggregation?.metrics || [])[resolved.metricIndex]?.filterId;
                if (!metricFilterId || (!state.from && !state.to))
                    return [];
                return [{ filterId: metricFilterId, from: state.from || '', to: state.to || '' }];
            }

            const filterId = resolved.identifier?.filterId;
            if (!filterId)
                return [];

            const selection: IDashFilterSelection = { filterId: filterId };
            if (state.values && state.values.length > 0)
                selection.values = state.values;
            if (state.from || state.to) {
                selection.from = state.from || '';
                selection.to = state.to || '';
            }

            return [selection];
        };

        const activeFilterHeaders = Object.keys(columnFilters).filter(h => isActiveFilter(columnFilters[h]));
        const hasActiveFilters = !isDesigner && activeFilterHeaders.length > 0;

        const headers = rawData?.Headers || [];
        const allRows = rawData?.ObjectResult || [];

        // With active filters an empty result means the filters excluded every row — keep rendering so the
        // filter pills (and their removal controls) stay reachable instead of a dead-end 'no data' state.
        if (allRows.length === 0 && !hasActiveFilters) {
            if ($openFilterPopover !== null && $openFilterPopover.data('filter-component') === component.id)
                closeTableFilterPopover();

            chartEl.append($('<div>').addClass('text-center text-muted p-3').text(T('No data to display.')));
            $(document).off(`click.tableExport_${component.id}`);
            element.find('.table-export-menu').remove();
            return;
        }

        const sortOverride = element.data('sortOverride') as ITableSortOverride | undefined;

        // Keep this table's open popover alive across re-renders, but close it when the underlying data changed
        if ($openFilterPopover !== null
            && $openFilterPopover.data('filter-component') === component.id
            && JSON.stringify(allRows) !== $openFilterPopover.data('filter-signature'))
            closeTableFilterPopover();

        // Server-paged windows (public view): the cache holds one fetch window of rows (the
        // first 500 by default); the pager pages within it and navigating past it fetches the
        // next window. TotalCount is the filtered row total, so the pager reflects the real
        // count — the designer keeps the full local result and pages it client-side.
        const pageSize = component.layout?.pageSize || 5;
        const isPaged = !isDesigner && rawData?.TotalCount !== undefined;
        const windowOffset = isPaged ? dataManager.getTableWindowOffset(component.id) : 0;
        const totalCount = isPaged ? Math.max(rawData.TotalCount || 0, allRows.length) : allRows.length;
        let currentPage = element.data('currentPage') || 1;
        const pendingPage = element.data('pendingPage') as number | undefined;
        const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
        if (pendingPage !== undefined) {
            element.removeData('pendingPage');
            if (pendingPage >= 1 && pendingPage <= totalPages)
                currentPage = pendingPage;
        }
        if (currentPage > totalPages)
            currentPage = totalPages;
        element.data('currentPage', currentPage);

        const absoluteStart = (currentPage - 1) * pageSize;
        const pageCovered = !isPaged || (absoluteStart >= windowOffset && absoluteStart < windowOffset + allRows.length);
        const pagedRows = pageCovered ? allRows.slice(absoluteStart - windowOffset, absoluteStart - windowOffset + pageSize) : [];

        const getTimeGrainForHeader = (header: string): InsightTimeGrain => {
            if (dataQuery.aggregation) {
                const groupBy = dataQuery.aggregation.groupBy;
                if (groupBy && getHeaderName(groupBy, component) === header)
                    return groupBy?.timeGrain ?? InsightTimeGrain.None;

                const breakDownBy = dataQuery.aggregation.breakDownBy;
                if (breakDownBy && getHeaderName(breakDownBy, component) === header)
                    return breakDownBy?.timeGrain ?? InsightTimeGrain.None;
            }

            if (dataQuery.projection && dataQuery.projection.columns) {
                const proj = dataQuery.projection.columns.find(c => getHeaderName(c, component) === header);
                if (proj)
                    return proj?.timeGrain ?? InsightTimeGrain.None;
            }

            return InsightTimeGrain.None;
        };

        // Sortable targets keyed by the header each column renders as, matched by column
        // identity (the same getHeaderName/getExpectedMetricHeader resolution the rest of
        // this file uses against headers) — never by position: the server's canonical
        // header order is not a contract the client should assume.
        const sortTargetByHeader = new Map<string, IDataQuerySortOrder>();
        const aggregation = dataQuery.aggregation;
        const hasAggregation = aggregation != null && (!!aggregation.groupBy?.columnId || !!aggregation.breakDownBy?.columnId || (aggregation.metrics?.length ?? 0) > 0);
        if (hasAggregation) {
            const groupBy = aggregation.groupBy;
            if (groupBy?.columnId) {
                const header = getHeaderName(groupBy, component);
                if (header)
                    sortTargetByHeader.set(header, { type: 'Asc', column: { datasetIndex: groupBy.datasetIndex, columnId: groupBy.columnId, alias: groupBy.alias, timeGrain: groupBy.timeGrain } });
            }

            const breakDownBy = aggregation.breakDownBy;
            if (breakDownBy?.columnId) {
                const header = getHeaderName(breakDownBy, component);
                if (header)
                    sortTargetByHeader.set(header, { type: 'Asc', column: { datasetIndex: breakDownBy.datasetIndex, columnId: breakDownBy.columnId, alias: breakDownBy.alias, timeGrain: breakDownBy.timeGrain } });
            }

            (aggregation.metrics ?? []).forEach((metric, index) => {
                const header = getExpectedMetricHeader(component, dataQuery, metric, index);
                if (header)
                    sortTargetByHeader.set(header, { type: 'Asc', metricIndex: index });
            });
        }
        else if (dataQuery.projection?.columns) {
            dataQuery.projection.columns.forEach(column => {
                const header = getHeaderName(column, component);
                if (header)
                    sortTargetByHeader.set(header, { type: 'Asc', column: { datasetIndex: column.datasetIndex, columnId: column.columnId, alias: column.alias, timeGrain: column.timeGrain } });
            });
        }

        const isSortableColumn = (header: string): boolean => {
            for (const row of allRows) {
                const value = row[header];
                if (value === null || value === undefined || value === '')
                    continue;

                return typeof value === 'number' || typeof value === 'string';
            }

            return false;
        };

        const cycleSort = (header: string, sortOrder: IDataQuerySortOrder) => {
            let next: ITableSortOverride | null;
            if (!sortOverride || sortOverride.header !== header)
                next = { header, dataQueryName: dataQueryPropName, orders: [{ ...sortOrder, type: 'Asc' }] };
            else if (sortOverride.orders[0]?.type === 'Asc')
                next = { header, dataQueryName: dataQueryPropName, orders: [{ ...sortOrder, type: 'Desc' }] };
            else
                next = null;

            if (next)
                element.data('sortOverride', next);
            else
                element.removeData('sortOverride');

            element.data('currentPage', 1);
            element.removeData('pendingPage');
            if (!isDesigner) {
                // A sort change re-runs the query — restart from the first window.
                dataManager.setTableWindowOffset(component.id, 0);
                render(element, component);
                eventBus.publish(EVENTS.DATA_REFRESH_REQUESTED, {
                    componentId: component.id,
                    viewName: cacheKey,
                    window: { offset: 0, limit: dataManager.getTableWindowSize() }
                });
                return;
            }
            render(element, component);
            eventBus.publish(EVENTS.DATA_REFRESH_REQUESTED, {
                componentId: component.id,
                viewName: cacheKey
            });
        };

        const getDisplayValue = (header: string, value: any): string => {
            if (value === null || value === undefined || value === '')
                return T('(blank)');

            const timeGrain = getTimeGrainForHeader(header);
            return timeGrain !== InsightTimeGrain.None ? distill.dashboards.formatDateByGrain(String(value), timeGrain) : String(value);
        };

        const persistFilters = () => {
            if (isDesigner)
                return;
            element.data('columnFilters', columnFilters);
        };

        const collectColumnFilters = (): IDashFilterSelection[] =>
            Object.keys(columnFilters).reduce((acc, h) => acc.concat(buildFilters(h)), [] as IDashFilterSelection[]);

        const triggerServerRefresh = () => {
            if (isDesigner)
                return;
            element.data('currentPage', 1);
            element.removeData('pendingPage');
            dataManager.setTableWindowOffset(component.id, 0);
            eventBus.publish(EVENTS.DATA_REFRESH_REQUESTED, {
                componentId: component.id,
                filters: collectColumnFilters(),
                window: { offset: 0, limit: dataManager.getTableWindowSize() }
            });
        };

        // A pane commit changes the option lists' context: drop the cached lists and mark
        // selection sets stale (revalidated on next popover open). No fetching here — the
        // pane's commit publishes DATA_REFRESH_REQUESTED without a componentId; the widget's
        // own refreshes carry one and are ignored.
        let globalFiltersSub = element.data('global-filters-sub');
        if (globalFiltersSub)
            eventBus.unsubscribe(globalFiltersSub);
        globalFiltersSub = eventBus.subscribe(EVENTS.DATA_REFRESH_REQUESTED, (payload: { componentId?: string; resetTablePaging?: boolean }) => {
            if (payload.componentId)
                return;

            distill.dashboards.filterOptionsStore.clearCaches(TABLE_COLUMN_KEY_PREFIX);
            distill.dashboards.filterOptionsStore.bumpGeneration();

            // New global filters invalidate every buffered window — restart from the first
            // page. (Auto-refresh publishes without resetTablePaging and keeps the page.)
            if (payload.resetTablePaging && !isDesigner)
                element.data('currentPage', 1);
        });
        element.data('global-filters-sub', globalFiltersSub);

        const buildFilterPills = (): JQuery => {
            const $bar = $('<div>').addClass('table-filter-pills d-flex flex-wrap align-items-center p-2 pb-0');
            $bar.append($('<span>').addClass('table-filter-pills-label small font-weight-bold text-muted mr-1').text(T('Active Filters')));

            activeFilterHeaders.forEach(header => {
                const state = columnFilters[header];

                // A column can carry two filter parts (values + range) — each gets its own
                // pill so removal is precise.
                const parts: { text: string, remove: () => void }[] = [];
                if (state.values && state.values.length > 0) {
                    parts.push({
                        text: state.values.map(key => getDisplayValue(header, key)).join(', '),
                        remove: () => {
                            delete state.values;
                            // The removed value is also remembered as a checked option - wipe that
                            // too, so re-opening the popover seeds fresh.
                            distill.dashboards.filterOptionsStore.deleteSelections(`${TABLE_COLUMN_KEY_PREFIX}${component.id}:${header}`);
                            if (!isActiveFilter(state))
                                delete columnFilters[header];
                        }
                    });
                }
                if (state.from || state.to) {
                    parts.push({
                        text: [state.from, state.to].filter(v => !!v).join(' \u2192 '),
                        remove: () => {
                            delete state.from;
                            delete state.to;
                            if (!isActiveFilter(state))
                                delete columnFilters[header];
                        }
                    });
                }

                parts.forEach(part => {
                    const $pill = $('<span>').addClass('table-filter-pill badge badge-pill badge-light').attr('title', `${header}: ${part.text}`);
                    $pill.append($('<span>').addClass('table-filter-pill-text').text(`${header}: ${part.text}`));
                    const $remove = $('<a>').addClass('table-filter-pill-remove').attr('href', 'javascript:void(0)').attr('title', T('Remove Filter')).html('<i class="fa fa-times"></i>');
                    $remove.on('click', () => {
                        part.remove();
                        persistFilters();
                        triggerServerRefresh();
                    });
                    $pill.append($remove);
                    $bar.append($pill);
                });
            });

            const $clearAll = $('<a>').addClass('table-filter-clear-all small ml-1').attr('href', 'javascript:void(0)').text(T('Clear All'));
            $clearAll.on('click', () => {
                Object.keys(columnFilters).forEach(h => delete columnFilters[h]);
                distill.dashboards.filterOptionsStore.deleteSelectionsByPrefix(`${TABLE_COLUMN_KEY_PREFIX}${component.id}:`);
                persistFilters();
                triggerServerRefresh();
            });
            $bar.append($clearAll);

            return $bar;
        };

        /** Creates the popover shell for a column's trigger: fixed-position card near the trigger, registered for outside-click closing. */
        const createFilterPopover = ($trigger: JQuery, header: string): JQuery => {
            const $popover = appn.utils.cloneTemplate('table-filter-popover');
            $popover.css({ visibility: 'hidden', position: 'fixed', top: 0, left: 0 }).appendTo('body');

            // The popover is appended to <body>, outside the tinted chart, so it carries the tint
            // itself (a table with a background colour gets a matching popover).
            if (hasBgColor)
                $popover.addClass('tinted').css(tintProperties);

            $popover.data('filter-trigger', $trigger[0]);
            $popover.data('filter-component', component.id);
            $popover.data('filter-header', header);
            $popover.data('filter-signature', JSON.stringify(allRows));
            $openFilterPopover = $popover;

            const rect = $trigger[0].getBoundingClientRect();
            const popoverWidth = $popover.outerWidth() || 260;
            const popoverHeight = $popover.outerHeight() || 320;
            const left = Math.min(Math.max(rect.left, 8), window.innerWidth - popoverWidth - 8);
            let top = rect.bottom + 4;
            if (top + popoverHeight > window.innerHeight - 8)
                top = Math.max(8, rect.top - popoverHeight - 4);
            $popover.css({ visibility: 'visible', left: left, top: top });

            $(document).on('mouseup.table-filter-popover', e => {
                // The date pickers move their calendar to <body> — clicks inside it must not
                // count as outside clicks or picking a date would dismiss the popover.
                if ($openFilterPopover && $(e.target).closest('.table-filter-popover').length === 0 && $(e.target).closest('.table-filter-trigger').length === 0 && $(e.target).closest('.bootstrap-datetimepicker-widget').length === 0)
                    closeTableFilterPopover();
            });

            return $popover;
        };

        /** Date-range variant of the filter popover: From/To pickers, blanks allowed. */
        const openDateFilterPopover = ($trigger: JQuery, header: string): void => {
            const state = columnFilters[header] || {};

            const $popover = createFilterPopover($trigger, header);

            const $dates = $popover.find('.table-filter-date-section').removeClass('d-none');
            const $from = $dates.find('.table-filter-date-from').val(state.from || '');
            const $to = $dates.find('.table-filter-date-to').val(state.to || '');

            const $actions = $popover.find('.table-filter-actions').removeClass('d-none').addClass('mt-2');
            const $apply = $actions.find('.table-filter-apply').prop('disabled', false);
            const $clear = $actions.find('.table-filter-clear');

            appn.ui.datePicker($popover.find('.datepicker'), undefined, 'YYYY-MM-DD');

            // Blanks allowed: only the picked bounds constrain the column.
            $apply.on('click', () => {
                const from = (($from.val() as string) || '').trim();
                const to = (($to.val() as string) || '').trim();
                if (!from && !to)
                    delete columnFilters[header];
                else
                    columnFilters[header] = { from: from, to: to };

                persistFilters();
                closeTableFilterPopover();
                triggerServerRefresh();
            });

            $clear.on('click', () => {
                delete columnFilters[header];
                persistFilters();
                closeTableFilterPopover();
                triggerServerRefresh();
            });
        };

        const openFilterPopover = ($trigger: JQuery, header: string) => {
            // Re-clicking the same column's trigger just closes the popover (toggle). Compare
            // by component + header — the trigger element is recreated on every re-render,
            // and .remove() wipes the removed node's jQuery data.
            const isSameColumn = $openFilterPopover !== null
                && $openFilterPopover.data('filter-header') === header
                && $openFilterPopover.data('filter-component') === component.id;
            closeTableFilterPopover();
            if (isSameColumn)
                return;

            if (getColumnForHeader(header)?.meta.type === 'Date') {
                openDateFilterPopover($trigger, header);
                return;
            }

            // Metric columns get a range-only popover: a count/sum has no distinct values to
            // list, so the value list, search and options fetch are all skipped.
            const metricIndex = getColumnForHeader(header)?.metricIndex;
            if (metricIndex !== null && metricIndex !== undefined) {
                openMetricFilterPopover($trigger, header, metricIndex);
                return;
            }

            const activeValues = (columnFilters[header]?.values) || null;

            // The column's selection lives in the shared filterOptionsStore (same store the
            // global filter pane uses), keyed by component + column: seeded from the committed
            // values on first open, then follows the user's checks.
            const selectionKey = `${TABLE_COLUMN_KEY_PREFIX}${component.id}:${header}`;
            if (!distill.dashboards.filterOptionsStore.hasSelections(selectionKey))
                distill.dashboards.filterOptionsStore.seedSelections(selectionKey, activeValues || []);

            const $popover = createFilterPopover($trigger, header);

            // Numeric columns add a From/To range above the value list; both parts AND-combine
            // (rows must match the selected values AND sit inside the range).
            let $rangeFrom: JQuery | null = null;
            let $rangeTo: JQuery | null = null;
            if (isNumericColumn(header)) {
                const rangeState = columnFilters[header] || {};
                const $range = $popover.find('.table-filter-number-section').removeClass('d-none');
                $rangeFrom = $range.find('.table-filter-range-from').val(rangeState.from || '');
                $rangeTo = $range.find('.table-filter-range-to').val(rangeState.to || '');
            }

            const $searchGroup = $popover.find('.table-filter-search-group').removeClass('d-none');
            const $search = $searchGroup.find('.table-filter-search');
            const $searchClear = $searchGroup.find('.table-filter-search-clear');
            const $loading = $popover.find('.table-filter-loading').removeClass('d-none');
            const $limited = $popover.find('.table-filter-limited');
            const $selectAll = $popover.find('.table-filter-select-all');
            const $selectAllCb = $selectAll.find('.table-filter-select-all-cb');
            const $values = $popover.find('.table-filter-values');
            // Whether the column has blanks, remembered from the unsearched render — a search can never match one.
            let hasBlanks = false;

            const $actions = $popover.find('.table-filter-actions').removeClass('d-none');
            const $apply = $actions.find('.table-filter-apply');
            const $clear = $actions.find('.table-filter-clear');

            const syncSelectAll = (): void => {
                const total = $values.children().length;
                const checked = $values.children().find('input[type="checkbox"]').filter(':checked').length;
                $selectAllCb.prop('checked', total > 0 && checked === total);
                $selectAllCb.prop('indeterminate', checked > 0 && checked < total);
            };

            $selectAll.on('change', 'input[type="checkbox"]', e => {
                const checked = $(e.target).prop('checked');
                $values.children().find('input[type="checkbox"]').prop('checked', checked);
                syncSelections();
                syncSelectAll();
            });

            $values.on('change', 'input[type="checkbox"]', () => {
                syncSelections();
                syncSelectAll();
            });

            const otherFilters: IDashFilterSelection[] = Object.keys(columnFilters)
                .filter(h => h !== header)
                .reduce((acc, h) => acc.concat(buildFilters(h)), [] as IDashFilterSelection[]);

            // The option list respects the viewer's applied global filters — a column already
            // constrained by a global filter only offers values that can match the data.
            const filters = [...distill.dashboards.globalFilterPane.getAppliedFilters(), ...otherFilters];

            // Server-side search over the shared option cache: the initial load shows the top
            // values and typing re-queries, so values beyond the first page stay findable. A
            // token guards against stale responses when the viewer keeps typing.
            let fetchToken = 0;

            const buildValue = (value: string, display: string, count: number | null, isChecked: boolean): JQuery => {
                const $row = $('<label>').addClass('table-filter-value').append(
                    $('<input type="checkbox">').val(value).prop('checked', isChecked),
                    $('<span>').addClass('table-filter-value-label').text(display)
                );

                // The blank shows a count only when the response carried one: a search never matches a blank, so its row has none.
                if (count !== null)
                    $row.append($('<span>').addClass('table-filter-count').text(String(count)));

                return $row;
            };

            // Every checkbox interaction reconciles the selection set: rendered values are
            // added/removed per their checked state (remembering their display label and
            // count); values not currently rendered are untouched and keep their stored state.
            const syncSelections = (): void => {
                const selections = distill.dashboards.filterOptionsStore.getSelections(selectionKey);
                $values.find('.table-filter-value').each((_, el) => {
                    const $cb = $(el).find('input[type="checkbox"]');
                    const value = String($cb.val());
                    if ($cb.prop('checked'))
                        selections.set(value, { display: $(el).find('.table-filter-value-label').text(), count: Number($(el).find('.table-filter-count').text()) || 0 });
                    else
                        selections.delete(value);
                });
            };

            const renderValues = (options: distill.dashboards.filterOptionsStore.IFilterValueOption[], search: string): void => {
                // A search filters what is SHOWN but never mutates the selection: checkboxes
                // render from the selection set, so a value checked under one search stays
                // checked when it is cleared or replaced. Unsearched renders also pin every
                // selection a truncated list dropped.
                const selections = distill.dashboards.filterOptionsStore.getSelections(selectionKey);
                options.forEach(o => {
                    if (selections.has(o.value))
                        selections.set(o.value, { display: getDisplayValue(header, o.value), count: o.count || 0 });
                });

                // Replace the previous results (kept visible until the re-query returned).
                $values.empty();

                // The blank is the first row: picked alone it filters to blanks, picked with values it adds them.
                const blankOption = options.find(o => o.value === '') || null;
                const valueOptions = options.filter(o => o.value !== '');

                if (search === '')
                    hasBlanks = blankOption !== null;
                if (hasBlanks || selections.has(''))
                    $values.append(buildValue('', T('(blank)'), blankOption?.count ?? null, selections.has('')));

                const known = new Set(valueOptions.map(o => o.value));
                if (search === '') {
                    selections.forEach((prior, value) => {
                        if (!known.has(value) && value !== '')
                            $values.append(buildValue(value, prior.display, prior.count, true));
                    });
                }
                valueOptions.forEach(item => $values.append(buildValue(item.value, getDisplayValue(header, item.value), item.count || 0, selections.has(item.value))));

                $selectAll.removeClass('d-none');
                $values.removeClass('d-none');

                // The server caps the list - tell the viewer the search can reach the rest.
                const truncated = valueOptions.length >= distill.dashboards.FILTER_OPTIONS_LIMIT;
                $limited.toggleClass('d-none', !truncated);
                if (truncated)
                    $limited.text(T('Showing the top results only - search to find more.'));

                $apply.prop('disabled', false);

                syncSelectAll();

                // Re-adjust top if popover expanded past window
                const newHeight = $popover.outerHeight() || 320;
                const currentTop = parseFloat($popover.css('top')) || 0;
                if (currentTop + newHeight > window.innerHeight - 8) {
                    const anchorTop = $trigger[0].getBoundingClientRect().top;
                    $popover.css('top', Math.max(8, anchorTop - newHeight - 4));
                }

                $apply.off('click').on('click', () => {
                    const selections = distill.dashboards.filterOptionsStore.getSelections(selectionKey);
                    const checkedKeys = Array.from(selections.keys());
                    const state = columnFilters[header] || {};

                    // A selection covering the complete unsearched list means "everything" —
                    // clear the values part. With a search active, or a truncated list, values
                    // exist beyond the page, so the explicit selection is committed instead.
                    // The range part (if any) always survives.
                    if (checkedKeys.length === 0 || (!search && !truncated && valueOptions.every(o => selections.has(o.value))))
                        delete state.values;
                    else
                        state.values = checkedKeys;

                    if ($rangeFrom && $rangeTo) {
                        const from = (($rangeFrom.val() as string) || '').trim();
                        const to = ($rangeTo.val() as string || '').trim();
                        if (from || to) {
                            state.from = from;
                            state.to = to;
                        } else {
                            delete state.from;
                            delete state.to;
                        }
                    }

                    if (!isActiveFilter(state))
                        delete columnFilters[header];
                    else
                        columnFilters[header] = state;

                    persistFilters();
                    closeTableFilterPopover();
                    triggerServerRefresh();
                });
            };

            const loadValues = (search: string): void => {
                const token = ++fetchToken;

                // The option list depends on the applied global and column filters, so the
                // cache key includes them. Truncated lists only serve the unsearched render —
                // searches keep going to the server.
                const cacheKey = `${selectionKey}:${JSON.stringify(filters)}`;
                const cached = distill.dashboards.filterOptionsStore.getCached(cacheKey, search);
                if (cached !== null) {
                    if (token !== fetchToken)
                        return;

                    $loading.addClass('d-none');
                    renderValues(cached, search);
                    return;
                }

                // A stale selection set rides along on the unsearched load: the response then
                // also carries `available` — which of the selected values still exist under
                // the applied global filters (works even for truncated lists, where the top page
                // alone cannot prove it). The set is pruned to the survivors before rendering.
                const revalidate = search === '' && distill.dashboards.filterOptionsStore.selectionsStale(selectionKey);
                const candidates = revalidate ? Array.from(distill.dashboards.filterOptionsStore.getSelections(selectionKey).keys()) : null;

                dataManager.fetchFilterOptions(component, header, filters, search, candidates).then(items => {
                    if (token !== fetchToken || $openFilterPopover !== $popover || !$.contains(document.body, $popover[0]))
                        return;

                    $loading.addClass('d-none');
                    if (items === null) {
                        $values.empty();
                        $selectAll.addClass('d-none');
                        $limited.removeClass('d-none').removeClass('text-muted').addClass('text-danger').text(T('An error occurred. Please try again.'));
                        return;
                    }

                    if (revalidate && items.available !== null) {
                        distill.dashboards.filterOptionsStore.markSelectionsValidated(selectionKey);

                        const selections = distill.dashboards.filterOptionsStore.getSelections(selectionKey);
                        const available = new Set(items.available.map(i => i.value));
                        selections.forEach((_, value) => {
                            if (!available.has(value))
                                selections.delete(value);
                        });
                    }

                    if (search === '')
                        distill.dashboards.filterOptionsStore.setCached(cacheKey, items.options);

                    renderValues(items.options, search);
                });
            };

            const syncSearchClear = (): void => {
                $searchClear.toggle((($search.val() as string) || '').trim().length > 0);
            };

            $search.on('input', () => {
                const term = (($search.val() as string) || '').trim();
                syncSearchClear();
                window.clearTimeout($search.data('searchTimer') as number);
                $search.data('searchTimer', window.setTimeout(() => loadValues(term), 300));
            });

            $searchClear.on('click', () => {
                $search.val('').trigger('input').trigger('focus');
            });

            syncSearchClear();
            loadValues('');

            $clear.on('click', () => {
                delete columnFilters[header];
                distill.dashboards.filterOptionsStore.deleteSelections(selectionKey);
                persistFilters();
                closeTableFilterPopover();
                triggerServerRefresh();
            });
        };

        const openMetricFilterPopover = ($trigger: JQuery, header: string, metricIndex: number) => {
            const $popover = createFilterPopover($trigger, header);
            const rangeState = columnFilters[header] || {};
            const $range = $popover.find('.table-filter-number-section').removeClass('d-none');
            const $rangeFrom = $range.find('.table-filter-range-from').val(rangeState.from || '');
            const $rangeTo = $range.find('.table-filter-range-to').val(rangeState.to || '');
            const $actions = $popover.find('.table-filter-actions').removeClass('d-none');
            const $apply = $actions.find('.table-filter-apply').prop('disabled', false);
            const $clear = $actions.find('.table-filter-clear');

            $apply.on('click', () => {
                const from = (($rangeFrom.val() as string) || '').trim();
                const to = (($rangeTo.val() as string) || '').trim();
                const state = columnFilters[header] || {};
                if (from || to) {
                    state.from = from;
                    state.to = to;
                } else {
                    delete state.from;
                    delete state.to;
                }

                if (!isActiveFilter(state))
                    delete columnFilters[header];
                else
                    columnFilters[header] = state;

                persistFilters();
                closeTableFilterPopover();
                triggerServerRefresh();
            });

            $clear.on('click', () => {
                delete columnFilters[header];
                persistFilters();
                closeTableFilterPopover();
                triggerServerRefresh();
            });
        };

        const renderTableContent = (rowsToRender: any[]) => {
            chartEl.empty();

            if (hasActiveFilters && allRows.length === 0) {
                chartEl.append(buildFilterPills());
                chartEl.append($('<div>').addClass('text-center text-muted p-3').text(T('No matching entries.')));
                return;
            }

            if (headers.length === 1) {
                const $list = $('<div>').addClass('list-widget p-2');
                const $ul = $('<ul>').addClass('list-group list-group-flush');
                const timeGrain = getTimeGrainForHeader(headers[0]);
                rowsToRender.forEach(row => {
                    let value = row[headers[0]];

                    if (value && timeGrain !== InsightTimeGrain.None)
                        value = distill.dashboards.formatDateByGrain(String(value), timeGrain);

                    $ul.append($('<li>').addClass('list-group-item').text(value !== null && value !== undefined ? String(value) : ''));
                });
                $list.append($ul);
                chartEl.append($list);
            } else if (headers.length > 1) {
                // Top padding (pt) is dropped: it would let rows slide above the sticky headers.
                const $tableContainer = $('<div>').addClass('table-responsive pr-2 pl-2 pb-2');
                // On a fill the stripes come from the derived custom property instead (see the
                // .chart.tinted rules), so the default table-striped banding is only added when
                // there is no fill to contrast against.
                const $table = $('<table>').addClass('table table-sm');
                if (!hasBgColor)
                    $table.addClass('table-striped');
                const $thead = $('<thead>').appendTo($table);
                const $tbody = $('<tbody>').appendTo($table);
                const $headerRow = $('<tr>').appendTo($thead);

                // Standard dashboard-table alignment: text and dates left, numbers right —
                // headers aligned to match their column. Dataset columns are typed; metric
                // columns (counts, sums) have no dataset column and are inferred from the data.
                const numericHeaders = new Set(headers.filter(isNumericColumn));
                // Right-aligned numbers get a padded right edge (table-number) so a value can't
                // sit flush against the next column's left-aligned text.
                const cellAlignClass = (header: string): string => numericHeaders.has(header) ? 'table-number' : '';

                headers.forEach(header => {
                    const sortOrder = sortTargetByHeader.get(header);
                    const $th = $('<th>');
                    const alignClass = cellAlignClass(header);
                    if (alignClass)
                        $th.addClass(alignClass);

                    if (sortOrder && isPublicView && isSortableColumn(header)) {
                        $th.addClass('table-sortable');

                        // Only the label and the direction indicator sort the column — they
                        // live in a dedicated target so the filter trigger and the cell's
                        // remaining space sit outside the sort's click area.
                        const $sortTarget = $('<span>').addClass('table-sort-target');

                        // Sort-direction indicator after the label, tri-state
                        // (asc/desc/default). The element always occupies its width, so
                        // cycling to/from the default state (no icon) doesn't shift the
                        // header layout.
                        const $sort = $('<span>').addClass('table-sort-direction');
                        if (sortOverride && sortOverride.header === header)
                            $sort.addClass(sortOverride.orders[0]?.type === 'Desc' ? 'sorting_desc' : 'sorting_asc');

                        $sortTarget.append($('<span>').addClass('table-header-label').text(header), $sort);
                        $sortTarget.on('click', () => cycleSort(header, sortOrder));
                        $th.append($sortTarget);
                    } else
                        $th.append($('<span>').addClass('table-header-label').text(header));

                    if (!isDesigner) {
                        const $trigger = $('<a>').addClass('table-filter-trigger').attr('href', 'javascript:void(0)').attr('title', T('Filter')).html('<i class="fa fa-filter"></i>');
                        if (isActiveFilter(columnFilters[header]))
                            $trigger.addClass('active');
                        // Sits beside (not inside) the sort target; keep the bubble stop so a
                        // future header-level handler can't hijack the trigger's clicks.
                        $trigger.on('click', e => {
                            e.preventDefault();
                            e.stopPropagation();
                            openFilterPopover($trigger, header);
                        });
                        $th.append($trigger);
                    }

                    $headerRow.append($th);
                });

                rowsToRender.forEach(row => {
                    const $row = $('<tr>').appendTo($tbody);
                    headers.forEach(header => {
                        let value = row[header];

                        const timeGrain = getTimeGrainForHeader(header);
                        if (value && timeGrain !== InsightTimeGrain.None)
                            value = distill.dashboards.formatDateByGrain(String(value), timeGrain);

                        const $td = $('<td>').text(value !== null && value !== undefined ? String(value) : '');
                        const alignClass = cellAlignClass(header);
                        if (alignClass)
                            $td.addClass(alignClass);
                        $row.append($td);
                    });
                });

                $tableContainer.append($table);
                chartEl.append($tableContainer);
            } else {
                chartEl.text('No columns selected.');
                return;
            }

            if (hasActiveFilters)
                chartEl.prepend(buildFilterPills());
        };

        const renderPagingControls = () => {
            if (totalPages <= 1 && !hasActiveFilters)
                return;

            const $pager = $('<div>').addClass('table-pager d-flex justify-content-center align-items-center flex-wrap p-2 small');

            if (totalPages > 1) {
                const $prev = $('<button>').addClass('btn btn-sm btn-outline-secondary mr-2').html('<i class="fa fa-chevron-left"></i>').prop('disabled', currentPage === 1);
                const $pageInfo = $('<span>').text(T('Page {0} of {1}', currentPage, totalPages));
                const $next = $('<button>').addClass('btn btn-sm btn-outline-secondary ml-2').html('<i class="fa fa-chevron-right"></i>').prop('disabled', currentPage === totalPages);

                const goToPage = (targetPage: number) => {
                    const targetStart = (targetPage - 1) * pageSize;
                    const covered = !isPaged || (targetStart >= windowOffset && targetStart < windowOffset + allRows.length);
                    if (covered) {
                        element.data('currentPage', targetPage);
                        render(element, component);
                        return;
                    }

                    // Outside the buffered window — fetch the aligned window covering the target
                    // page; the refresh's response re-renders onto the requested page.
                    const windowSize = dataManager.getTableWindowSize();
                    const baseOffset = Math.floor(targetStart / windowSize) * windowSize;
                    dataManager.setTableWindowOffset(component.id, baseOffset);
                    element.data('pendingPage', targetPage);
                    eventBus.publish(EVENTS.DATA_REFRESH_REQUESTED, {
                        componentId: component.id,
                        viewName: cacheKey,
                        window: { offset: baseOffset, limit: windowSize }
                    });
                };

                $prev.on('click', () => goToPage(currentPage - 1));
                $next.on('click', () => goToPage(currentPage + 1));

                $pager.append($prev, $pageInfo, $next);
            }

            const totalEntries = totalCount;
            const startEntry = totalEntries === 0 ? 0 : absoluteStart + 1;
            const endEntry = Math.min(absoluteStart + pageSize, totalEntries);
            const summaryText = hasActiveFilters
                ? T('Showing {0} to {1} of {2} filtered entries', startEntry, endEntry, totalEntries)
                : T('Showing {0} to {1} of {2} entries', startEntry, endEntry, totalEntries);
            $pager.append($('<span>').addClass('table-entry-summary text-muted ml-2').text(summaryText));

            chartEl.append($pager);
        };

        const renderExportMenu = () => {
            if (!distill.dashboards.isPublicView || !distill.dashboards.exportUrl)
                return;

            const eventNamespace = `tableExport_${component.id}`;
            $(document).off(`click.${eventNamespace}`);

            const $header = element.find('.card-header');
            if (!$header.length)
                return;

            $header.find('.table-export-menu').remove();

            const $menuContainer = $('<div>')
                .addClass('table-export-menu dropdown');

            const $btn = $('<button>')
                .addClass('btn btn-sm btn-tool dropdown-toggle')
                .attr({
                    'type': 'button',
                    'aria-haspopup': 'true',
                    'aria-expanded': 'false',
                    'title': T('Export Data')
                })
                .html('<i class="fa fa-ellipsis-v"></i>');

            const $dropdownMenu = $('<div>')
                .addClass('dropdown-menu dropdown-menu-right shadow-sm');

            // Result sets beyond the server's export cap: tell the viewer before they export.
            const EXPORT_ROW_CAP = 50000;
            if (totalCount > EXPORT_ROW_CAP)
                $dropdownMenu.append($('<div>').addClass('table-export-note px-3 py-2 small text-muted').text(T('Only the first 50,000 rows will be exported.')));

            const $csvItem = $('<a>')
                .addClass('dropdown-item export-csv-item')
                .attr('href', 'javascript:void(0);')
                .html(`<i class="fa fa-file-text-o mr-2 text-muted"></i>${T('Export to CSV (.csv)')}`);

            const $excelItem = $('<a>')
                .addClass('dropdown-item export-excel-item')
                .attr('href', 'javascript:void(0);')
                .html(`<i class="fa fa-file-excel-o mr-2 text-success"></i>${T('Export to Excel (.xlsx)')}`);

            const closeMenu = () => {
                $menuContainer.removeClass('show');
                $dropdownMenu.removeClass('show');
                $btn.attr('aria-expanded', 'false');
            };

            $btn.on('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const isShowing = $dropdownMenu.hasClass('show');
                $('.table-export-menu .dropdown-menu.show').removeClass('show');
                $('.table-export-menu.show').removeClass('show');
                if (!isShowing) {
                    $menuContainer.addClass('show');
                    $dropdownMenu.addClass('show');
                    $btn.attr('aria-expanded', 'true');
                } else {
                    closeMenu();
                }
            });

            $(document).on(`click.${eventNamespace}`, (e) => {
                if (!$(e.target).closest($menuContainer).length)
                    closeMenu();
            });

            // Both formats are written server-side now: the API rebuilds the view from the design (component +
            // viewer's filters + sort) and returns the file, so the client never ships or formats the rows.
            // The file name's extension tells the server which format to write.
            const runExport = async (extension: 'csv' | 'xlsx') => {
                const fileName = distill.dashboards.getExportFileName(component, extension);
                // Excel caps worksheet names at 31 characters — the server enforces the same limit
                const MAX_SHEET_NAME_LENGTH = 31;
                const sheetName = (component.label?.eng || component.title?.eng || 'Data').substring(0, MAX_SHEET_NAME_LENGTH);

                // Ensure the token is fresh before the request goes out.
                if (!await distill.dashboards.publicView.ensureValidAccessToken())
                    return;

                distill.dashboards.exportTable({
                    url: distill.dashboards.exportUrl,
                    accessToken: distill.dashboards.publicView.getAccessToken() || '',
                    fileName: fileName,
                    sheetName: sheetName,
                    componentId: component.id,
                    filters: [...distill.dashboards.globalFilterPane.getAppliedFilters(), ...collectColumnFilters()],
                    sortOverrides: distill.dashboards.widgets.collectSortOverrides()
                });
            };

            $csvItem.on('click', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                closeMenu();
                await runExport('csv');
            });

            $excelItem.on('click', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                closeMenu();
                await runExport('xlsx');
            });

            $dropdownMenu.append($csvItem, $excelItem);
            $menuContainer.append($btn, $dropdownMenu);
            $header.append($menuContainer);
        };

        renderTableContent(pagedRows);
        renderPagingControls();
        renderExportMenu();
    }

    // Chart handles are owned per widget instance (passed in by the caller) so clearing or re-rendering
    // one Ask widget cannot dispose another widget's charts, and every ResizeObserver is disconnected.
    interface IChatChartHandle {
        instance: echarts.ECharts;
        observer: ResizeObserver;
    }

    function disposeChatCharts(charts: IChatChartHandle[]) {
        charts.forEach(c => {
            try {
                c.observer.disconnect();
                c.instance.dispose();
            }
            catch { /* ignore */ }
        });
        charts.length = 0;
    }

    function renderChatChart($message: JQuery, chart: IAskChart, component: IDashboardComponent, charts: IChatChartHandle[]) {
        const $container = $('<div class="distill-chat-chart"></div>');
        $message.append($container);

        const chartEl = $container.get(0) as HTMLDivElement;
        if (!chartEl)
            return;

        const colors = resolveChatColors(component);
        const options = buildChatChartOptions(chart, colors);

        try {
            const instance = echarts.init(chartEl);
            instance.setOption(options, true);

            const resizeObserver = new ResizeObserver(() => {
                instance.resize();
            });
            resizeObserver.observe(chartEl);

            charts.push({ instance, observer: resizeObserver });
        }
        catch { /* ignore init errors */ }
    }

    function resolveChatColors(component: IDashboardComponent): string[] {
        if (component.styling?.colorPalette === 'Custom' && component.styling?.customColorPalette?.length)
            return component.styling.customColorPalette;
        const paletteName = component.styling?.colorPalette || 'Vibrant';
        return DashColorPalettes[paletteName] || DashColorPalettes['Vibrant'];
    }

    // Shared visual defaults aligned to the dashboard chart renderer (chart.ts) so ASK widget charts
    // look consistent with dashboard charts. Deliberately duplicated (not imported) to keep the chat
    // chart renderer decoupled from the designer component pipeline (AHA). If the two ever drift
    // visibly, extract shared constants — not the stateful chart.ts pipeline.
    const CHAT_CHART_DEFAULTS = {
        fontColor: '#666666',
        fontSize: 14,
        tooltipBg: 'rgba(50, 50, 50, 0.95)',
        tooltipTextColor: '#ffffff',
        axisLineColor: '#ccc',
        splitLineColor: '#eee',
        outlineColor: '#ffffff',
        barMaxWidth: 20
    };

    function buildChatChartOptions(chart: IAskChart, colors: string[]): any {
        const type = (chart.Type || 'bar').toLowerCase();
        const labels = chart.Labels || [];
        const series = chart.Series || [];

        const options: any = {
            color: colors,
            tooltip: {
                trigger: type === 'pie' ? 'item' : 'axis',
                confine: true,
                appendTo: 'body',
                borderWidth: 0,
                padding: 10,
                backgroundColor: CHAT_CHART_DEFAULTS.tooltipBg,
                textStyle: { color: CHAT_CHART_DEFAULTS.tooltipTextColor }
            },
            grid: { top: '15%', bottom: '10%', left: '10%', right: '10%', containLabel: true }
        };

        const baseLegend = {
            type: 'scroll' as const,
            textStyle: { color: CHAT_CHART_DEFAULTS.fontColor, fontSize: CHAT_CHART_DEFAULTS.fontSize },
            itemStyle: { borderColor: CHAT_CHART_DEFAULTS.outlineColor, borderWidth: 2 }
        };

        if (type === 'pie') {
            const pieData = labels.map((label, i) => ({
                name: label,
                value: series[0]?.Data?.[i] ?? 0
            }));

            options.legend = { ...baseLegend, bottom: 0 };
            options.series = [{
                type: 'pie',
                radius: ['0%', '75%'],
                center: ['50%', '50%'],
                itemStyle: { borderRadius: 5, borderColor: CHAT_CHART_DEFAULTS.outlineColor, borderWidth: 2 },
                label: { show: true, formatter: '{b}: {d}%', fontSize: CHAT_CHART_DEFAULTS.fontSize },
                data: pieData
            }];
        }
        else {
            const stacked = chart.Stacked === true;
            const isLine = type === 'line';
            options.legend = { ...baseLegend, bottom: 0 };
            options.xAxis = {
                type: 'category',
                data: labels,
                boundaryGap: !isLine,
                axisLabel: {
                    rotate: labels.length > 6 ? 30 : 0,
                    color: CHAT_CHART_DEFAULTS.fontColor,
                    fontSize: CHAT_CHART_DEFAULTS.fontSize,
                    interval: 'auto',
                    hideOverlap: true
                },
                axisLine: { lineStyle: { color: CHAT_CHART_DEFAULTS.axisLineColor } },
                axisTick: { show: false }
            };
            options.yAxis = {
                type: 'value',
                axisLabel: { color: CHAT_CHART_DEFAULTS.fontColor, fontSize: CHAT_CHART_DEFAULTS.fontSize },
                splitLine: { lineStyle: { type: 'dashed', color: CHAT_CHART_DEFAULTS.splitLineColor } },
                axisLine: { show: false },
                axisTick: { show: false }
            };
            options.series = series.map(s => ({
                name: s.Name,
                type: isLine ? 'line' : 'bar',
                data: s.Data,
                smooth: isLine,
                connectNulls: isLine,
                stack: stacked ? 'total' : undefined,
                barMaxWidth: CHAT_CHART_DEFAULTS.barMaxWidth,
                itemStyle: { borderRadius: type === 'bar' ? (stacked ? [0, 0, 0, 0] : [3, 3, 0, 0]) : undefined }
            }));
        }

        return options;
    }

    export function collectSortOverrides(): IViewSortOverride[] {
        const overrides: IViewSortOverride[] = [];

        const visit = (components: IComponent[]) => {
            for (const component of components) {
                if (component.type === ComponentType.table) {
                    const override = $(`#${component.id}`).data('sortOverride') as ITableSortOverride | undefined;
                    if (override && override.orders.length > 0)
                        overrides.push({ componentId: component.id, dataQueryName: override.dataQueryName, sortOrders: override.orders });
                }

                if (component.children)
                    visit(component.children);
            }
        };

        if (designer?._design?.components)
            visit(designer._design.components);

        return overrides;
    }

    function renderDistillQuery(element: JQuery, chartEl: JQuery, component: IDashboardComponent) {

        // Lightweight Markdown renderer with sanitization for AI responses.
        // We intentionally avoid external libraries to keep scope minimal (YAGNI/AHA) and
        // only enable Markdown for model/bot messages. User/loading messages remain plain text.
        function renderMarkdownToSafeHtml(md: string): string {
            if (!md)
                return '';

            // 1. Normalize Newlines
            let text = md.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

            // 2. Escape HTML characters FIRST (Security)
            const escapeHtml = (s: string) => s
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

            text = escapeHtml(text);

            // 3. Extract "Real" Code Blocks (e.g. JS, Python, SQL)
            // We pull these out now so our formatting logic doesn't mess up the code inside.
            const codeBlocks: { placeholder: string; html: string; }[] = [];
            let codeIndex = 0;

            text = text.replace(/```(\w+)?\s*\n([\s\S]*?)```/g, (_m, lang, code) => {
                const idx = codeIndex++;
                const placeholder = `@@DISTILLCODEBLOCK${idx}@@`;
                const language = (lang || '').trim().toLowerCase();
                const langClass = language ? ` class="language-${language}"` : '';

                // AdminLTE styled Code Card
                const html = `
                    <div class="card my-2 code-card border-light shadow-sm">
                        ${language ? `<div class="card-header py-1 px-2 bg-light text-muted small text-uppercase font-weight-bold">${language}</div>` : ''}
                        <div class="card-body p-0">
                            <pre class="m-0 rounded-0 bg-white"><code${langClass} class="d-block p-3 text-dark" style="font-size: 0.9em;">${code}</code></pre>
                        </div>
                    </div>`;

                codeBlocks.push({ placeholder, html });
                return placeholder;
            });

            // 4. Process Global Inline Elements

            // Images: ![Alt](Url) -> Added Bootstrap img-fluid
            text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="img-fluid rounded shadow-sm my-2" />');

            // Links: [Text](Url "Title")
            text = text.replace(/\[([^\]]+)\]\(([^)\s]+)(?:\s+(?:"|&quot;)([^"]+)(?:"|&quot;))?\)/g, (_m, txt, url, title) => {
                const titleAttr = title ? ` title="${title}"` : '';
                return `<a href="${url}"${titleAttr} target="_blank" rel="noopener noreferrer" class="text-primary font-weight-bold">${txt}</a>`;
            });

            // Bold: **text** or __text__
            text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
            text = text.replace(/__([^_]+)__/g, '<strong>$1</strong>');

            // Italic: *text* or _text_
            text = text.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
            text = text.replace(/(^|[^a-zA-Z0-9])_([^_]+)_(?![a-zA-Z0-9])/g, '$1<em>$2</em>');

            // Strikethrough: ~~text~~
            text = text.replace(/~~([^~]+)~~/g, '<del>$1</del>');

            // Inline Code: `text` -> styled <code>
            text = text.replace(/`([^`\n]+)`/g, '<code class="text-danger bg-light px-1 rounded" style="font-size: 0.9em;">$1</code>');

            // 5. Line-by-Line Block Processing
            const lines = text.split('\n');
            let htmlOut = '';

            // State Flags
            let inUl = false;
            let inOl = false;
            let inBlockquote = false;
            let inTable = false;

            const closeAll = () => {
                if (inUl) { htmlOut += '</ul>'; inUl = false; }
                if (inOl) { htmlOut += '</ol>'; inOl = false; }
                if (inBlockquote) { htmlOut += '</blockquote>'; inBlockquote = false; }
                if (inTable) { htmlOut += '</tbody></table></div>'; inTable = false; }
            };

            for (let i = 0; i < lines.length; i++) {
                const rawLine = lines[i];
                const line = rawLine.trimEnd();
                const trimmed = line.trim();

                // Code Block Placeholder Check
                if (/^@@DISTILLCODEBLOCK\d+@@$/.test(trimmed)) {
                    closeAll();
                    htmlOut += trimmed;
                    continue;
                }

                // Empty Lines
                if (trimmed.length === 0) {
                    closeAll();
                    continue;
                }

                // Horizontal Rule (---, ***, ___)
                if (/^(\*{3,}|-{3,}|_{3,})$/.test(trimmed)) {
                    closeAll();
                    htmlOut += '<hr class="my-4" />';
                    continue;
                }

                // Headings (# H1 to ###### H6)
                const hMatch = /^\s*(#{1,6})\s+(.*)$/.exec(line);
                if (hMatch) {
                    closeAll();
                    const level = hMatch[1].length;
                    htmlOut += `<h${level} class="mt-4 mb-2 font-weight-bold">${hMatch[2]}</h${level}>`;
                    continue;
                }

                // Blockquotes (> Text)
                const qMatch = /^&gt;\s?(.*)$/.exec(trimmed);
                if (qMatch) {
                    closeAll(); // Close lists/tables
                    if (!inBlockquote) {
                        htmlOut += '<blockquote class="blockquote p-3 bg-light border-left border-primary ml-0 my-3" style="border-width: 4px !important; font-size: 1rem;">';
                        inBlockquote = true;
                    } else {
                        htmlOut += '<br/>';
                    }
                    htmlOut += qMatch[1];
                    continue;
                }
                if (inBlockquote) { htmlOut += '</blockquote>'; inBlockquote = false; }

                // Unordered Lists (*, -, +)
                const ulMatch = /^\s*[-*+]\s+(.*)$/.exec(line);
                if (ulMatch) {
                    if (inOl) { htmlOut += '</ol>'; inOl = false; }
                    if (inTable) { htmlOut += '</tbody></table></div>'; inTable = false; }
                    if (!inUl) { htmlOut += '<ul class="pl-4 mb-3">'; inUl = true; }
                    htmlOut += `<li>${ulMatch[1]}</li>`;
                    continue;
                }

                // Ordered Lists (1. Item)
                const olMatch = /^\s*\d+\.\s+(.*)$/.exec(line);
                if (olMatch) {
                    if (inUl) { htmlOut += '</ul>'; inUl = false; }
                    if (inTable) { htmlOut += '</tbody></table></div>'; inTable = false; }
                    if (!inOl) { htmlOut += '<ol class="pl-4 mb-3">'; inOl = true; }
                    htmlOut += `<li>${olMatch[1]}</li>`;
                    continue;
                }

                // Tables (Lines starting and ending with |)
                if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
                    // Detect Header Separator (---|---|---) — skip before touching any block state
                    if (/^\|?[\s-:]+(\|[\s-:]+)*\|?$/.test(trimmed))
                        continue;

                    // Close non-table blocks (lists, blockquotes) but preserve open table state
                    if (inUl) {
                        htmlOut += '</ul>';
                        inUl = false;
                    }
                    if (inOl) {
                        htmlOut += '</ol>';
                        inOl = false;
                    }
                    if (inBlockquote) {
                        htmlOut += '</blockquote>';
                        inBlockquote = false;
                    }

                    const rowContent = trimmed.split('|').map(c => c.trim()).filter((c, idx, arr) => {
                        if (idx === 0 && c === '')
                            return false;
                        if (idx === arr.length - 1 && c === '')
                            return false;
                        
                        return true;
                    });

                    if (!inTable) {
                        htmlOut += '<div class="table-responsive my-3"><table class="table table-bordered table-sm table-striped bg-white"><thead><tr>';
                        rowContent.forEach(cell => { htmlOut += `<th>${cell}</th>`; });
                        htmlOut += '</tr></thead><tbody>';
                        inTable = true;
                    } else {
                        htmlOut += '<tr>';
                        rowContent.forEach(cell => { htmlOut += `<td>${cell}</td>`; });
                        htmlOut += '</tr>';
                    }
                    continue;
                }
                if (inTable) { htmlOut += '</tbody></table></div>'; inTable = false; }

                // Default: Paragraph
                closeAll();
                htmlOut += `<p class="mb-2 text-wrap text-break">${line}</p>`;
            }
            closeAll();

            // 6. Restore Code Blocks
            for (const block of codeBlocks) {
                htmlOut = htmlOut.split(block.placeholder).join(block.html);
            }

            // 7. Final Sanitize
            const sanitizeHtml = (unsafeHtml: string): string => {
                const temp = document.createElement('div');
                temp.innerHTML = unsafeHtml;

                const allowed: { [tag: string]: string[] } = {
                    div: ['class', 'style'], pre: ['class', 'style'], code: ['class', 'style'],
                    p: ['class'], br: [], strong: [], em: [], del: [],
                    ul: ['class'], ol: ['class'], li: [],
                    blockquote: ['class', 'style'], hr: ['class'],
                    table: ['class'], thead: [], tbody: [], tr: [], th: [], td: [],
                    h1: ['class'], h2: ['class'], h3: ['class'], h4: ['class'], h5: ['class'], h6: ['class'],
                    a: ['href', 'title', 'target', 'rel', 'class'],
                    img: ['src', 'alt', 'title', 'class']
                };

                const isAllowedTag = (tag: string) => Object.prototype.hasOwnProperty.call(allowed, tag);

                const walk = (node: Node) => {
                    const nodeType = node.nodeType;
                    if (nodeType === Node.ELEMENT_NODE) {
                        const el = node as HTMLElement;
                        const tag = el.tagName.toLowerCase();
                        if (!isAllowedTag(tag)) {
                            const replacement = document.createTextNode(el.textContent || '');
                            el.parentNode?.replaceChild(replacement, el);
                            return;
                        }
                        const allowedAttrs = allowed[tag];
                        const attrs = Array.from(el.attributes);
                        for (const a of attrs) {
                            if (allowedAttrs.indexOf(a.name) === -1) el.removeAttribute(a.name);
                        }
                        // Force link safety
                        if (tag === 'a') {
                            const href = el.getAttribute('href') || '';
                            const lower = href.trim().toLowerCase();
                            // Allow absolute http(s)/mailto links, in-page anchors, and same-origin root-relative
                            // paths (e.g. Url.Action results like "/Users/Edit/1"). Protocol-relative "//host"
                            // links are rejected.
                            const safe = lower.startsWith('http://') || lower.startsWith('https://') || lower.startsWith('mailto:')
                                || (lower.startsWith('/') && !lower.startsWith('//')) || lower.startsWith('#');
                            if (!safe) el.removeAttribute('href');
                        }
                        const children = Array.from(el.childNodes);
                        for (const child of children) walk(child);
                    } else if (nodeType === Node.DOCUMENT_FRAGMENT_NODE || nodeType === Node.DOCUMENT_NODE) {
                        const children = Array.from(node.childNodes);
                        for (const child of children) walk(child);
                    }
                };
                walk(temp);
                return temp.innerHTML;
            };

            return sanitizeHtml(htmlOut);
        }

        let welcomeMessage = component.welcomeMessage;
        if (!welcomeMessage || welcomeMessage.trim() === '')
            welcomeMessage = 'Hello!\nHow can I help you?';

        const styling = component.styling || {};
        const buttonColor = styling.sendButtonStyle;
        const buttonTextColor = styling.sendButtonTextColor;
        const buttonIcon = 'fa-play';
        const responseColor = styling.responseMessageBgColor;
        const responseTextColor = styling.responseMessageTextColor;
        const sentColor = styling.sentMessageBgColor;
        const sentTextColor = styling.sentMessageTextColor;
        const bgColor = component.bgColor || 'transparent';
        const headerBgColor = styling.headerBgColor;
        const inputBgColor = styling.inputBgColor || '#ffffff';

        let chatHistory: distill.dashboards.IChatMessage[] = element.data('chatHistory') || [];
        let chatHistorySignature: string | null = element.data('chatSignature') || null;
        // Chart data is stored alongside the history (index-aligned) so a re-render can restore the charts a
        // previous answer rendered; the signed history itself carries only the turn text.
        let chatChartData: (IAskChart[] | null)[] = element.data('chatCharts') || [];

        // Track this widget's chat charts locally so AI_QUERY_CLEAR (and re-render) only dispose this
        // component's charts. Dispose anything left by a previous render of the same component first.
        disposeChatCharts(element.data('chat-chart-handles') || []);
        const chatCharts: IChatChartHandle[] = [];
        element.data('chat-chart-handles', chatCharts);

        let $clearButton: JQuery;
        const $header = element.find('.card-header');
        if ($header.length > 0) {
            if (headerBgColor)
                $header.css('background-color', headerBgColor);

            const $label = $header.find('.componentLabel');
            if ($label.length > 0 && $label.find('.ask-beta-tag').length === 0)
                $label.append('<span class="ask-beta-tag badge badge-warning ml-2" style="vertical-align: middle;">' + T('Beta') + '</span>');

            $header.css('position', 'relative');

            $clearButton = $header.find('.clear-chat-btn');
            if ($clearButton.length === 0) {

                $clearButton = $(`
                    <a href="#" class="btn btn-sm btn-icon-only clear-chat-btn" title="Clear Chat" 
                       style="position: absolute; top: 50%; right: 8px; transform: translateY(-50%); margin: 0;">
                        <i class="fa fa-eraser"></i>
                    </a>
                `);

                $clearButton.on('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    eventBus.publish(DASH_EVENTS.AI_QUERY_CLEAR, { componentId: component.id });
                });

                $header.append($clearButton);
            }
        }

        chartEl.append(`
            <div class="distill-query-widget-wrapper" style="background-color: ${bgColor};">
                <div class="distill-query-log"></div>
                <div class="modal-footer">
                    <div class="input-group">
                        <input type="text" class="chat-input form-control" placeholder="Ask a question..." 
                               style="background-color: ${inputBgColor};">
                        <div class="input-group-append">
                            <button class="send-button btn" title="Send" type="button" 
                                    style="background-color: ${buttonColor}; border-color: ${buttonColor}; color: ${buttonTextColor};">
                                <i class="fa ${buttonIcon}"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `);

        let $sendButton = chartEl.find('.send-button');
        let $chatInput = chartEl.find('.chat-input');
        let $chatLog = chartEl.find('.distill-query-log');

        $chatLog.empty();
        addMessageToLog(welcomeMessage!, 'model');
        chatHistory.forEach((message, i) => {
            const messageCharts = chatChartData[i];
            if (message.Role === 'model' && messageCharts && messageCharts.length > 0)
                appendAssistantMessage(message.Parts[0].Text, messageCharts);
            else
                addMessageToLog(message.Parts[0].Text, message.Role);
        });

        $sendButton.on('click', () => sendMessage());
        $chatInput.on('keypress', (e) => {
            if (e.key !== 'Enter')
                return;

            e.preventDefault();
            sendMessage();
        });

        let $loadingEl: JQuery | null = null;

        let sendEventSub = element.data('send-event-sub');
        if(sendEventSub)
            eventBus.unsubscribe(sendEventSub);
        sendEventSub = eventBus.subscribe(DASH_EVENTS.AI_QUERY_SEND, (payload: IAiQuerySendPayload) => {
            if (payload.component.id !== component.id)
                return;

            $chatInput.val('');
            $chatInput.prop('disabled', true);
            $sendButton.prop('disabled', true);
            $clearButton?.prop('disabled', true);

            addMessageToLog(payload.userQuery, 'user');
            $loadingEl = addMessageToLog("thinking...", 'loading');
        });
        element.data('send-event-sub', sendEventSub);

        let resultEventSub = element.data('result-event-sub');
        if(resultEventSub)
            eventBus.unsubscribe(resultEventSub);
        resultEventSub = eventBus.subscribe(DASH_EVENTS.AI_QUERY_RESULT, (result: IAiQueryResultPayload) => {
            if (result.componentId !== component.id)
                return;

            try {
                $loadingEl?.remove();

                let answer: string | null = null;
                let charts: IAskChart[] | null = null;

                if (result.success && result.response){
                    const parsed: IAskQueryResponse = typeof result.response === 'string' ? JSON.parse(result.response) : result.response;
                    if (parsed.ErrorType === InternalDistillErrorType.UsageLimitExceeded) {
                        // The user turn was never added to the signed history (it is display-only), so there is nothing
                        // to roll back here.

                        // Match the other AI chat surfaces: a plain message. Users who can change their own user
                        // type get the upgrade action as an inline link; everyone else is told to ask their administrator.
                        const action = result.upgradeUrl
                            ? ` [${T('Upgrade to Pro')}](${result.upgradeUrl})`
                            : ` ${T('Contact your administrator to upgrade your user account.')}`;
                        addMessageToLog(`${parsed.Answer || ''}${action}`, 'bot');
                        return;
                    }

                    if (parsed.Answer)
                        answer = parsed.Answer;

                    if (parsed.Charts && parsed.Charts.length > 0)
                        charts = parsed.Charts;

                    const newTurns = parsed.Turns;
                    if (newTurns && newTurns.length > 0) {
                        chatHistory.push(...newTurns);
                        // Align chart data with the appended turns: only the trailing model turn carries charts.
                        newTurns.forEach((turn, i) =>
                            chatChartData.push(i === newTurns.length - 1 ? (charts || []) : null));
                        chatHistorySignature = parsed.HistorySignature || null;
                        element.data('chatHistory', chatHistory);
                        element.data('chatSignature', chatHistorySignature);
                        element.data('chatCharts', chatChartData);
                    }
                }

                appendAssistantMessage(answer || '', charts || []);
            }
            finally {
                // Always re-enable the chat controls - including the usage-limit early return above and any error -
                // so the widget never gets stuck disabled (e.g. after the user upgrades to Pro, without a refresh).
                $chatInput.prop('disabled', false);
                $sendButton.prop('disabled', false);
                $clearButton?.prop('disabled', false);
                $chatInput.trigger('focus');
            }
        });
        element.data('result-event-sub', resultEventSub);

        let clearEventSub = element.data('clear-event-sub');
        if(clearEventSub)
            eventBus.unsubscribe(clearEventSub);
        clearEventSub = eventBus.subscribe(DASH_EVENTS.AI_QUERY_CLEAR, (payload: { componentId: string }) => {
            if (payload.componentId !== component.id)
                return;

            chatHistory = []; // Reset internal history
            chatHistorySignature = null;
            chatChartData = [];
            disposeChatCharts(chatCharts);
            $chatLog.empty(); // Clear UI

            addMessageToLog(welcomeMessage!, 'model');
            element.data('chatHistory', chatHistory); // Save cleared history
            element.data('chatSignature', chatHistorySignature);
            element.data('chatCharts', chatChartData);
            $chatInput.val('').prop('disabled', false);
            $sendButton.prop('disabled', false);
            if ($clearButton) $clearButton.prop('disabled', false);

            if ($loadingEl) {
                $loadingEl.remove();
                $loadingEl = null;
            }

            $chatInput.trigger('focus');

        });
        element.data('clear-event-sub', clearEventSub);

        function sendMessage(){
            const userQuery = $chatInput.val() as string;
            if (!userQuery || userQuery.trim() === '')
                return;

            // The user turn is display-only here; the server derives the signed turns and returns them to append.
            eventBus.publish(DASH_EVENTS.AI_QUERY_SEND, {
                component: component,
                userQuery: userQuery,
                history: chatHistory,
                historySignature: chatHistorySignature
            });
        }

        function addMessageToLog(content: string, type: 'user'|'bot'|'loading'|'model'): JQuery {
            const $message = $('<div class="distill-message"></div>');
            const displayType = (type === 'model') ? 'bot' : type;

            $message.addClass(displayType);
            const safeContent = content || "";

            // Only bot/model messages render Markdown to HTML. Others remain plain text for safety and clarity.
            if (displayType === 'bot' && type !== 'loading')
                $message.html(renderMarkdownToSafeHtml(safeContent));
            else
                $message.text(safeContent);

            if (displayType === 'user') {
                $message.css('background-color', sentColor!);
                $message.css('color', sentTextColor!);
                $message.css('text-align', 'right');
            } else { // 'bot' or 'loading'
                $message.css('background-color', responseColor!);
                $message.css('color', responseTextColor!);
                $message.css('text-align', 'left');
            }

            $chatLog.append($message);
            $chatLog.scrollTop($chatLog[0].scrollHeight);

            return $message;
        }

        // Renders an assistant answer into the log, splitting the <!--chart--> markers and rendering each
        // embedded chart. Shared by the live result handler and the re-render rebuild so charts survive a
        // component re-render.
        function appendAssistantMessage(answer: string, charts: IAskChart[]): void {
            if (answer) {
                if (charts.length > 0 && answer.indexOf('<!--chart-->') !== -1) {
                    const segments = answer.split('<!--chart-->');
                    const $msg = addMessageToLog('', 'bot');
                    $msg.empty();

                    for (let i = 0; i < segments.length; i++) {
                        const seg = segments[i].trim();
                        if (seg)
                            $msg.append($(renderMarkdownToSafeHtml(seg)));

                        if (i < charts.length)
                            renderChatChart($msg, charts[i], component, chatCharts);
                    }

                    if (charts.length > segments.length - 1) {
                        for (let i = segments.length - 1; i < charts.length; i++)
                            renderChatChart($msg, charts[i], component, chatCharts);
                    }
                }
                else {
                    const $msg = addMessageToLog(answer, 'bot');

                    if (charts.length > 0)
                        charts.forEach(chart => renderChatChart($msg, chart, component, chatCharts));
                }
            }
            else if (charts.length > 0) {
                const $msg = addMessageToLog('', 'bot');
                $msg.empty();
                charts.forEach(chart => renderChatChart($msg, chart, component, chatCharts));
            }
            else {
                addMessageToLog(T('Sorry, something went wrong. Please try again.'), 'bot');
            }
        }
    }
}
